"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { Dialog, DialogContent, DialogTitle } from "@v1/ui/dialog";
import { cn } from "@v1/ui/utils";
import { useQuery } from "convex/react";
import { ArrowRight, CornerDownLeft, Search, Sparkles, X } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { type KeyboardEvent, type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useHelpChrome } from "./help-chrome-context";
import { useHelpHref } from "./help-link-base";

const DEBOUNCE_MS = 160;
const ESC_KEY_CAP = "esc"; // i18n-exempt: keyboard key cap, printed on the key itself
const SUGGESTIONS = 6;

interface ResultRow {
  readonly pageId: string;
  readonly title: string;
  readonly sub: string;
}

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState<string>(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [value]);
  return debounced;
}

const SKELETON_ROWS: ReadonlyArray<{ readonly key: string; readonly title: string; readonly sub: string }> = [
  { key: "a", title: "w-2/5", sub: "w-4/5" },
  { key: "b", title: "w-1/3", sub: "w-3/5" },
  { key: "c", title: "w-1/2", sub: "w-2/3" },
  { key: "d", title: "w-1/4", sub: "w-3/4" },
];

/** Same box as a result row, so hits swap in without a jump. */
function ResultSkeleton(): ReactElement {
  return (
    <ul className="flex animate-pulse flex-col gap-1" aria-hidden>
      {SKELETON_ROWS.map((row) => (
        <li key={row.key} className="flex flex-col gap-0.5 px-4 py-3">
          <span className="flex h-[22px] items-center">
            <span className={cn("h-3.5 rounded bg-muted/80", row.title)} />
          </span>
          <span className="flex h-[18px] items-center">
            <span className={cn("h-3 rounded bg-muted/55", row.sub)} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border/80 bg-background px-1 font-sans text-[11px] text-primary/60 shadow-[0_1px_0_rgba(0,0,0,0.08)]">
      {children}
    </kbd>
  );
}

/** Search window of the help center: dimmed page, big field, „Frag die KI“, results, key hints. */
export function HelpSearchDialog({ orgSlug }: { readonly orgSlug: string }): ReactElement {
  const t = useTranslations("public.helpSearchDialog");
  const { searchOpen, setSearchOpen, ask, chatAvailable } = useHelpChrome();
  const helpHref = useHelpHref(orgSlug);
  const [text, setText] = useState<string>("");
  const [selected, setSelected] = useState<number>(0);
  const typed = text.trim();
  const query = useDebounced(typed);
  const searching = query.length >= 2;
  const search = useQuery(
    api.pages.public_help_search.searchPublicHelp,
    searchOpen && searching ? { org_slug: orgSlug, query } : "skip",
  );
  const center = useQuery(
    api.pages.public_help.getPublicHelpCenter,
    searchOpen && !searching ? { org_slug: orgSlug } : "skip",
  );
  // Pending debounce counts as loading, so the skeleton shows without delay.
  const loading = typed.length >= 2 ? typed !== query || search === undefined : !searching && center === undefined;
  const linkRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  const rows = useMemo((): ResultRow[] => {
    if (loading) return [];
    if (searching) {
      return (search?.results ?? []).map((hit) => ({ pageId: hit.page_id, title: hit.title, sub: hit.snippet }));
    }
    if (center?.state !== "ok") return [];
    return center.spaces
      .flatMap((space) => space.pages.filter((p) => p.depth === 0).map((p) => ({ pageId: p.page_id, title: p.title, sub: space.name })))
      .slice(0, SUGGESTIONS);
  }, [center, loading, search, searching]);

  const showAsk = chatAvailable && text.trim().length > 0;
  const offset = showAsk ? 1 : 0;
  const count = rows.length + offset;

  useEffect(() => setSelected(0), [query, showAsk]);
  useEffect(() => {
    if (!searchOpen) setText("");
  }, [searchOpen]);
  useEffect(() => {
    linkRefs.current[selected - offset]?.scrollIntoView({ block: "nearest" });
  }, [offset, selected]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "ArrowDown" && count > 0) {
      event.preventDefault();
      setSelected((s) => (s + 1) % count);
    } else if (event.key === "ArrowUp" && count > 0) {
      event.preventDefault();
      setSelected((s) => (s - 1 + count) % count);
    } else if (event.key === "Enter" && count > 0) {
      event.preventDefault();
      if (showAsk && selected === 0) ask(text);
      else linkRefs.current[selected - offset]?.click();
    }
  };

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent
        hideCloseButton
        disableViewportLock
        overlayClassName="bg-black/40 backdrop-blur-[2px]"
        className="top-[10vh] w-[calc(100%-1.5rem)] max-w-[720px] translate-y-0 gap-0 rounded-2xl border-border/60 p-0 shadow-2xl data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2"
      >
        <DialogTitle className="sr-only">{t("titel")}</DialogTitle>
        <div className="flex max-h-[min(680px,80vh)] flex-col">
          <div className="p-4 pb-3">
            <div className="flex h-14 items-center gap-3 rounded-xl border-2 border-action-500/35 bg-background px-4 focus-within:border-action-500/60">
              <Search className="h-5 w-5 shrink-0 text-action-600" strokeWidth={2.2} aria-hidden />
              <input
                // biome-ignore lint/a11y/noAutofocus: the dialog opens for typing a search
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("platzhalter")}
                aria-label={t("titel")}
                enterKeyHint="search"
                className="min-w-0 flex-1 bg-transparent text-[17px] text-primary/90 placeholder:text-primary/35 focus:outline-none"
              />
              {text.length > 0 ? (
                <button
                  type="button"
                  aria-label={t("leeren")}
                  onClick={() => setText("")}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-primary/45 transition-colors hover:bg-muted hover:text-primary/75"
                >
                  <X className="h-[18px] w-[18px]" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
            {showAsk ? (
              <button
                type="button"
                onClick={() => ask(text)}
                onMouseEnter={() => setSelected(0)}
                className={cn(
                  "mb-3 block w-full rounded-xl bg-gradient-to-r from-action-600 via-action-500 to-[#04AC98] p-[2px] text-left transition-shadow",
                  selected === 0 ? "shadow-[0_0_0_3px_rgba(0,128,112,0.15)]" : "opacity-95",
                )}
              >
                <span className="flex items-center gap-4 rounded-[10px] bg-background px-5 py-4">
                  <Sparkles className="h-6 w-6 shrink-0 text-action-600" strokeWidth={1.8} aria-hidden />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[16px] text-primary/90">{t("kiFrage", { query: text.trim() })}</span>
                    <span className="text-[13px] text-primary/55">{t("kiUnterzeile")}</span>
                  </span>
                  <ArrowRight className="h-5 w-5 shrink-0 text-primary/60" aria-hidden />
                </span>
              </button>
            ) : null}

            {searching && !loading && search !== undefined && rows.length === 0 ? (
              <p className="px-1 py-6 text-center text-[13.5px] text-primary/45">{t("keineTreffer")}</p>
            ) : null}

            {loading ? <ResultSkeleton /> : null}

            <ul className="flex flex-col gap-1">
              {rows.map((row, index) => {
                const active = selected === index + offset;
                return (
                  <li key={row.pageId}>
                    <Link
                      ref={(el) => {
                        linkRefs.current[index] = el;
                      }}
                      href={helpHref({ kind: "page", pageId: row.pageId })}
                      prefetch={false}
                      onClick={() => setSearchOpen(false)}
                      onMouseEnter={() => setSelected(index + offset)}
                      className={cn(
                        "flex items-center gap-4 rounded-xl px-4 py-3 transition-colors",
                        active ? "bg-action-500/[0.06] ring-1 ring-inset ring-action-500/30" : "hover:bg-muted/50",
                      )}
                    >
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-[15px] font-semibold text-primary/90">{row.title}</span>
                        {row.sub ? (
                          <span className="line-clamp-2 text-[13px] leading-snug text-primary/55">{row.sub}</span>
                        ) : null}
                      </span>
                      {active ? <CornerDownLeft className="h-4 w-4 shrink-0 text-primary/40" aria-hidden /> : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="hidden shrink-0 items-center gap-5 border-t border-border/50 bg-muted/30 px-5 py-2.5 text-[12px] text-primary/50 sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <Kbd>↵</Kbd>
              {t("oeffnen")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd>↓</Kbd>
              <Kbd>↑</Kbd>
              {t("auswaehlen")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd>{ESC_KEY_CAP}</Kbd>
              {t("schliessen")}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
